import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { OpsApiError, opsApiRequest } from '../api/client.ts';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { LocaleSwitcher, useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * App chrome — sidebar + header + main area. Renders the authenticated
 * experience for every operator-facing page under `/`.
 *
 * Visual contract: DESIGN.md §5 — 256px sidebar on `surface-container-low`,
 * 56px header on `surface-container-high`, `surface` main area with
 * `space-8` padding. Depth is expressed through background tone only —
 * no 1px section borders.
 *
 * Children are optional: the default route tree nests pages under a
 * single `<Shell />` with `<Outlet />`, but the existing auth wrapper
 * still passes explicit children (legacy shape). Both paths work.
 *
 * Labels + tooltips resolve through `useT()` so the chrome flips
 * between zh-CN and en when the user toggles the header switcher.
 * `data-testid` values stay stable (derived from `testId`, not the
 * displayed label) so E2E selectors keep working across locales.
 */
interface NavItem {
  readonly to: string;
  readonly labelKey: I18nKey;
  readonly testId: string;
  readonly icon: string;
  readonly end?: boolean;
}

interface NavGroup {
  readonly titleKey: I18nKey;
  readonly items: readonly NavItem[];
}

/**
 * Sidebar navigation grouped by role-based workspace (Epic #109
 * Cross-cutting). These are the canonical operator routes; legacy flat
 * URLs remain redirect-only shims in App.tsx.
 *
 *   Overview    — business + route health entrypoints
 *   Customers   — tenant operations, usage, request forensics
 *   Finance     — money flows, reconciliation, revenue
 *   Supply      — upstream/model/channel supply chain
 *   Governance  — audit, operator accounts, system operations
 */
const NAV_GROUPS: readonly NavGroup[] = [
  {
    titleKey: 'shell.group.overview',
    items: [
      { to: '/overview', labelKey: 'shell.nav.overview', testId: 'overview', icon: 'space_dashboard', end: true },
      { to: '/overview/route-health', labelKey: 'shell.nav.route_health', testId: 'route-health', icon: 'monitor_heart' },
    ],
  },
  {
    titleKey: 'shell.group.customers',
    items: [
      { to: '/customers/accounts', labelKey: 'shell.nav.accounts', testId: 'accounts', icon: 'account_circle' },
      { to: '/customers/usage', labelKey: 'shell.nav.customer_usage', testId: 'customer-usage', icon: 'query_stats' },
      { to: '/customers/requests', labelKey: 'shell.nav.request_logs', testId: 'request-logs', icon: 'article' },
      { to: '/customers/failed-deductions', labelKey: 'shell.nav.failed_deductions', testId: 'failed-deductions', icon: 'error' },
      { to: '/customers/billing-anomalies', labelKey: 'shell.nav.billing_anomalies', testId: 'billing-anomalies', icon: 'rule' },
    ],
  },
  {
    titleKey: 'shell.group.finance',
    items: [
      { to: '/finance/revenue', labelKey: 'shell.nav.revenue', testId: 'revenue', icon: 'payments' },
      { to: '/finance/payment-orders', labelKey: 'shell.nav.payments_orders', testId: 'payment-orders', icon: 'receipt_long' },
      { to: '/finance/payment-providers', labelKey: 'shell.nav.payment_providers', testId: 'payment-providers', icon: 'credit_card' },
      { to: '/finance/gift-cards', labelKey: 'shell.nav.gift_cards', testId: 'gift-cards', icon: 'redeem' },
      { to: '/finance/fx-policy', labelKey: 'shell.nav.fx_policy', testId: 'fx-policy', icon: 'currency_exchange' },
      { to: '/finance/reports', labelKey: 'shell.nav.reports', testId: 'reports', icon: 'assessment' },
    ],
  },
  {
    titleKey: 'shell.group.supply',
    items: [
      { to: '/supply/upstreams', labelKey: 'shell.nav.upstreams', testId: 'upstreams', icon: 'dns' },
      { to: '/supply/models', labelKey: 'shell.nav.models', testId: 'models', icon: 'view_carousel' },
      { to: '/supply/vendors', labelKey: 'shell.nav.vendors', testId: 'vendors', icon: 'storefront' },
      { to: '/supply/channels', labelKey: 'shell.nav.channels', testId: 'channels', icon: 'hub' },
    ],
  },
  {
    titleKey: 'shell.group.governance',
    items: [
      { to: '/governance/audit', labelKey: 'shell.nav.audit', testId: 'audit', icon: 'history' },
      { to: '/governance/ops-users', labelKey: 'shell.nav.ops_users', testId: 'ops-users', icon: 'manage_accounts' },
      { to: '/system/webhook-events', labelKey: 'shell.nav.webhook_events', testId: 'webhook-events', icon: 'webhook' },
      { to: '/system/cron-runs', labelKey: 'shell.nav.cron_runs', testId: 'cron-runs', icon: 'schedule' },
      { to: '/system/dr-check', labelKey: 'shell.nav.dr_check', testId: 'dr-check', icon: 'health_and_safety' },
    ],
  },
];

const ALL_NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

interface Props {
  children?: ReactNode;
}

export function Shell({ children }: Props): JSX.Element {
  const { user, clearUser, fetchImpl } = useSession();
  const location = useLocation();
  const t = useT();

  const pageTitle = useMemo(() => {
    const match = [...ALL_NAV_ITEMS]
      .sort((a, b) => b.to.length - a.to.length)
      .find((item) => (item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)));
    return match !== undefined ? t(match.labelKey) : t('shell.page.console_fallback');
  }, [location.pathname, t]);

  async function handleLogout(): Promise<void> {
    try {
      await opsApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Even if /logout fails we clear the client-side mirror so the UI
      // doesn't stay in an inconsistent "logged in" state. The cookie
      // will expire on its own server-side TTL.
      if (!(err instanceof OpsApiError)) {
        throw err;
      }
    }
    clearUser();
  }

  const displayEmail =
    user?.email !== undefined && user.email.length > 0
      ? user.email
      : t('shell.signed_in_fallback');
  const primaryRole = user?.roles[0];

  return (
    <div className="flex h-screen overflow-hidden bg-surface text-on-surface">
      <aside
        data-testid="shell-sidebar"
        className="flex min-h-0 w-64 shrink-0 flex-col bg-surface-container-low px-4 py-6"
      >
        <div className="mb-8 flex shrink-0 items-center gap-3 px-2">
          <BrandLogo className="h-9 w-9 shrink-0 text-on-surface" />
          <div className="flex min-w-0 flex-col">
            <span className="block truncate text-lg font-black tracking-tighter text-on-surface">
              {t('shell.brand.title')}
            </span>
            <span className="mt-0.5 block truncate text-[11px] font-semibold uppercase tracking-[0.08em] text-primary">
              {t('shell.brand.subtitle')}
            </span>
          </div>
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain"
        >
          {NAV_GROUPS.map((group) => (
            <div key={group.titleKey} className="flex flex-col gap-1">
              <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t(group.titleKey)}
              </span>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end === true || item.to === '/'}
                  data-testid={`shell-nav-${item.testId}`}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low',
                      isActive
                        ? 'bg-surface-container text-primary'
                        : 'text-outline hover:bg-surface-container hover:text-on-surface',
                    ].join(' ')
                  }
                >
                  <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
                    {item.icon}
                  </span>
                  <span>{t(item.labelKey)}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="mt-auto flex shrink-0 flex-col gap-3 pt-6">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-tertiary">
            <span
              aria-hidden="true"
              className="h-2 w-2 rounded-full bg-tertiary animate-pulse-tertiary"
            />
            <span>{t('shell.system_status')}</span>
          </div>
          <div className="flex flex-col gap-1 rounded-lg bg-surface-container px-3 py-3">
            <span
              className="truncate text-xs font-semibold text-on-surface"
              data-testid="shell-email"
            >
              {displayEmail}
            </span>
            <span className="inline-flex w-fit rounded-full bg-surface-container-highest px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
              {primaryRole !== undefined && primaryRole.length > 0
                ? primaryRole
                : t('shell.role.fallback')}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="shell-logout"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-surface-container-highest px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-low"
          >
            <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
              logout
            </span>
            <span>{t('shell.logout')}</span>
          </button>
        </div>
      </aside>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between bg-surface-container-high px-8">
          <h1
            className="text-base font-semibold tracking-tight text-on-surface"
            data-testid="shell-page-title"
          >
            {pageTitle}
          </h1>
          <div className="flex items-center gap-4">
            <label className="flex h-8 items-center gap-2 rounded-lg bg-surface-container-lowest px-3 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-surface-container-high">
              <span
                aria-hidden="true"
                className="material-symbols-outlined text-[16px] text-outline"
              >
                search
              </span>
              <input
                type="search"
                placeholder={t('shell.header.search.placeholder')}
                aria-label={t('shell.header.search.aria_label')}
                className="w-48 bg-transparent text-[12px] text-on-surface placeholder:text-outline focus:outline-none"
              />
            </label>
            <LocaleSwitcher />
            <button
              type="button"
              aria-label={t('shell.header.notifications.aria_label')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-outline transition-colors hover:bg-surface-container hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container-high"
            >
              <span aria-hidden="true" className="material-symbols-outlined text-[20px]">
                notifications
              </span>
            </button>
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
